const PLATFORM_ADMIN_PERMISSION = 'manage:studio';
/** Creator and Admin Access grant this; mirrors the API's INTEGRATION_BUILDER_PERMISSION. */
const INTEGRATION_BUILDER_PERMISSION = 'build:studio-integrations';
/** Admin Access grants this; mirrors the API's INTEGRATION_APPROVER_PERMISSION. */
const INTEGRATION_APPROVER_PERMISSION = 'approve:studio-integrations';
/** Developer, Creator and Admin Access grant this; mirrors the API's TEMPLATE_CURATOR_PERMISSION. */
const TEMPLATE_CURATOR_PERMISSION = 'curate:studio-templates';
/** Developer and Admin Access grant this; mirrors the API's CODE_EDIT_PERMISSION. */
const CODE_EDIT_PERMISSION = 'edit:studio-code';
/** Developer and Admin Access grant this; mirrors the API's REPO_IMPORT_PERMISSION. */
const REPO_IMPORT_PERMISSION = 'import:studio-repos';
/** Every access bundle grants this; mirrors the API's STUDIO_INTERNAL_PERMISSION. */
const STUDIO_INTERNAL_PERMISSION = 'access:studio-internal';
/** Developer, Creator and Admin Access grant this; mirrors the API's SKILL_CURATOR_PERMISSION. */
const SKILL_CURATOR_PERMISSION = 'curate:studio-skills';
/** Admin Access grants this; mirrors the API's STUDIO_ADMIN_PERMISSION. */
const STUDIO_ADMIN_PERMISSION = 'admin:studio';

export type AccessTokenClaims = {
  permissions?: unknown;
  scope?: unknown;
  [key: string]: unknown;
};

export function decodeAccessTokenClaims(token: string): AccessTokenClaims | null {
  const [, payload] = token.split('.');
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(globalThis.atob(padded)) as AccessTokenClaims;
  } catch {
    return null;
  }
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(/\s+/).filter(Boolean);
  }
  return [];
}

function claimPermissions(claims: AccessTokenClaims): string[] {
  return [...asStringArray(claims.permissions), ...asStringArray(claims.scope)];
}

export function isPlatformAdminFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  if (!claims) return false;
  return claimPermissions(claims).includes(PLATFORM_ADMIN_PERMISSION);
}

/** Platform admins build integrations too; the builder role alone grants only that. */
export function canBuildIntegrationsFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  if (!claims) return false;
  const permissions = claimPermissions(claims);
  return (
    permissions.includes(PLATFORM_ADMIN_PERMISSION) ||
    permissions.includes(INTEGRATION_BUILDER_PERMISSION) ||
    permissions.includes(INTEGRATION_APPROVER_PERMISSION)
  );
}

/** Approvers approve, publish and unpublish integrations, their own included. */
export function canApproveIntegrationsFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  if (!claims) return false;
  const permissions = claimPermissions(claims);
  return permissions.includes(PLATFORM_ADMIN_PERMISSION) || permissions.includes(INTEGRATION_APPROVER_PERMISSION);
}

/** Platform admins curate templates too; the curator role alone grants only the review queue. */
export function canCurateTemplatesFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  if (!claims) return false;
  const permissions = claimPermissions(claims);
  return permissions.includes(PLATFORM_ADMIN_PERMISSION) || permissions.includes(TEMPLATE_CURATOR_PERMISSION);
}

function holdsOrAdmin(claims: AccessTokenClaims | null | undefined, permission: string): boolean {
  if (!claims) return false;
  const permissions = claimPermissions(claims);
  return permissions.includes(PLATFORM_ADMIN_PERMISSION) || permissions.includes(permission);
}

export function canEditStudioCodeFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  return holdsOrAdmin(claims, CODE_EDIT_PERMISSION);
}

export function canImportReposFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  return holdsOrAdmin(claims, REPO_IMPORT_PERMISSION);
}

/** Sees items whose audience is `internal`; using one in an app also needs a RapidSOS organization. */
export function canSeeStudioInternalFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  return holdsOrAdmin(claims, STUDIO_INTERNAL_PERMISSION);
}

export function canCurateSkillsFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  return holdsOrAdmin(claims, SKILL_CURATOR_PERMISSION);
}

/** The Studio (not Studio Admin) roles a token carries, for showing who can do what. */
export type StudioRoleAccess = { canEditCode: boolean; canImportRepos: boolean; canSeeInternal: boolean; canCurateSkills: boolean };

export const NO_STUDIO_ROLES: StudioRoleAccess = { canEditCode: false, canImportRepos: false, canSeeInternal: false, canCurateSkills: false };

/** Every permission a token carries, from `permissions` and `scope`. */
export function permissionsFromToken(token: string): string[] {
  const claims = decodeAccessTokenClaims(token);
  return claims ? claimPermissions(claims) : [];
}

export function studioRolesFromToken(token: string): StudioRoleAccess {
  return studioRolesFromPermissions(permissionsFromToken(token));
}

export function studioRolesFromPermissions(permissions: readonly string[]): StudioRoleAccess {
  const claims: AccessTokenClaims = { permissions: [...permissions] };
  return {
    canEditCode: canEditStudioCodeFromClaims(claims),
    canImportRepos: canImportReposFromClaims(claims),
    canSeeInternal: canSeeStudioInternalFromClaims(claims),
    canCurateSkills: canCurateSkillsFromClaims(claims),
  };
}

/** Studio Admin's Home, Organizations and Users, without its Operations. Platform admins have it too. */
export function isStudioAdminFromClaims(claims: AccessTokenClaims | null | undefined): boolean {
  if (!claims) return false;
  const permissions = claimPermissions(claims);
  return permissions.includes(PLATFORM_ADMIN_PERMISSION) || permissions.includes(STUDIO_ADMIN_PERMISSION);
}

export type AdminAccess = {
  isPlatformAdmin: boolean;
  /** Admin Access: everything in Studio Admin except Operations. */
  isStudioAdmin: boolean;
  canBuildIntegrations: boolean;
  canApproveIntegrations: boolean;
  canCurateTemplates: boolean;
  canCurateSkills: boolean;
};

export const NO_ACCESS: AdminAccess = {
  isPlatformAdmin: false,
  isStudioAdmin: false,
  canBuildIntegrations: false,
  canApproveIntegrations: false,
  canCurateTemplates: false,
  canCurateSkills: false,
};

export function adminAccessFromToken(token: string): AdminAccess {
  return adminAccessFromPermissions(permissionsFromToken(token));
}

export function adminAccessFromPermissions(permissions: readonly string[]): AdminAccess {
  const claims: AccessTokenClaims = { permissions: [...permissions] };
  return {
    isPlatformAdmin: isPlatformAdminFromClaims(claims),
    isStudioAdmin: isStudioAdminFromClaims(claims),
    canBuildIntegrations: canBuildIntegrationsFromClaims(claims),
    canApproveIntegrations: canApproveIntegrationsFromClaims(claims),
    canCurateTemplates: canCurateTemplatesFromClaims(claims),
    canCurateSkills: canCurateSkillsFromClaims(claims),
  };
}

/** Any Studio Admin role at all; without one the app shows Forbidden. */
export function hasAnyAdminAccess(access: AdminAccess): boolean {
  return access.isPlatformAdmin || access.isStudioAdmin || access.canBuildIntegrations || access.canCurateTemplates || access.canCurateSkills;
}

/** The admin roles a person holds, in words, for the account menu. */
export function adminRoleLabel(access: AdminAccess): string {
  if (access.isPlatformAdmin) return 'Platform admin';
  if (access.isStudioAdmin) return 'Studio admin';
  const roles = [
    access.canApproveIntegrations ? 'Integration approver' : access.canBuildIntegrations ? 'Integration builder' : undefined,
    access.canCurateTemplates ? 'Template curator' : undefined,
    access.canCurateSkills ? 'Skill curator' : undefined,
  ].filter((role): role is string => Boolean(role));
  return roles.length > 0 ? roles.join(' · ') : 'No admin role';
}

/** What an integration builder without platform admin may open. */
const BUILDER_PATHS = ['/integrations/builder'];

export function isBuilderPath(path: string): boolean {
  return BUILDER_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export const BUILDER_HOME = '/integrations/builder';

/** What a template curator without platform admin may open. */
const CURATOR_PATHS = ['/templates'];

export function isCuratorPath(path: string): boolean {
  return CURATOR_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export const CURATOR_HOME = '/templates';

/** What a skill curator without platform admin may open. */
const SKILL_CURATOR_PATHS = ['/skills'];

export function isSkillCuratorPath(path: string): boolean {
  return SKILL_CURATOR_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export const SKILL_CURATOR_HOME = '/skills';

/**
 * Operations: Platform, Runs, Fabrics, Token health, AI, and one organization's Fabric, sessions and
 * runs. Platform admins only; a studio admin sees everything else.
 */
const OPERATIONS_PATHS = ['/platform', '/runs', '/fabrics', '/token-health', '/ai', '/cowork', '/ops'];
const ORG_OPERATIONS_PATH = /^\/organizations\/[^/]+\/(?:fabric|sessions|runs)(?:\/|$)/;

export function isOperationsPath(path: string): boolean {
  return OPERATIONS_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`)) || ORG_OPERATIONS_PATH.test(path);
}

export function canOpenPath(path: string, access: AdminAccess): boolean {
  return (
    access.isPlatformAdmin ||
    (access.isStudioAdmin && !isOperationsPath(path)) ||
    (access.canBuildIntegrations && isBuilderPath(path)) ||
    (access.canCurateTemplates && isCuratorPath(path)) ||
    (access.canCurateSkills && isSkillCuratorPath(path))
  );
}

/** Where someone without platform admin lands. */
export function limitedHome(access: AdminAccess): string {
  if (access.canBuildIntegrations) return BUILDER_HOME;
  return access.canCurateTemplates ? CURATOR_HOME : SKILL_CURATOR_HOME;
}
